import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rowActionLabel } from '../../lib/deleteLabel'
import { useLocalizedText } from '../../lib/localizedText'
import { Dropdown } from 'primereact/dropdown'
import { TabPanel, TabView } from 'primereact/tabview'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { templateParamGroupColumns } from './components/TemplateParamGroupColumns'
import TemplateParamGroupFormDialog from './components/TemplateParamGroupFormDialog'
import type { TemplateParamGroupFormValues } from './components/templateParamGroupFormSchema'
import { exportTemplateParamGroups } from './api/templateParamGroupApi'
import { useTemplateParamGroups } from './hooks/useTemplateParamGroups'
import { templateParameterColumns } from './components/TemplateParameterColumns'
import TemplateParameterFormDialog from './components/TemplateParameterFormDialog'
import type { TemplateParameterFormValues } from './components/templateParameterFormSchema'
import { exportTemplateParameters, getTemplateParameter } from './api/templateParameterApi'
import { useTemplateParameters } from './hooks/useTemplateParameters'
import { useTableSort } from '../../lib/useTableSort'
import type {
  TemplateParamGroup,
  TemplateParamGroupUpsert,
  TemplateParameter,
  TemplateParameterUpsert,
} from './types'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import { InputSwitch } from 'primereact/inputswitch'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/*
 * **Parametrelerde de yalnız DÜZENLEME vardır** (2026-08-04 kararı — grupların 2026-08-03 kararının
 * aynısı): ekranda **ekleme**, **silme** ve **durum değiştirme** yoktur. Parametreler şablon
 * metnindeki yer tutucuların kataloğudur; bir kodun karşılığı gönderim tarafında **kodla** çözülür →
 * kodda karşılığı olmayan yeni bir kayıt hiçbir şey ifade etmez, var olan bir kaydın silinmesi ise
 * yazılmış şablonlardaki yer tutucuyu sessizce çözülemez bırakır. Bu yüzden katalogda satır
 * ekleme/çıkarma veri tabanı tarafında yapılır; ekranda yalnız **metinler** (ad, açıklama) ve
 * **statik davranış** düzenlenir.
 */

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/** "Tümü" sentinel'i — PrimeReact Dropdown `null`/`''` değerli seçeneği "boş seçim" sayıp yutar. */
const ALL = -1

/** Sekme indeksleri — grup listesinin tembel yüklenmesi buna bağlıdır (research D12). */
const TAB_PARAMETERS = 0
const TAB_GROUPS = 1

/**
 * Admin → **Şablon Parametreleri** (059). Tek sayfa, **iki sekme**: *Parametreler* (varsayılan) ve
 * *Gruplar*.
 *
 * **Her sekme kendi filtre, sayfa ve sıralama durumunu korur** (FR-022); sekme değişimi diğerinin
 * durumunu sıfırlamaz. **Grup listesi tembel yüklenir** — Gruplar sekmesine girilene kadar istek
 * atılmaz. Grup **combosu** bundan ayrıdır ve her zaman yüklenir (Parametreler sekmesinin filtresidir).
 *
 * **Kolon sıralaması sunucu-taraflıdır** (`useTableSort` + `SortSupport` beyaz listesi): tablolar
 * `lazy` olduğundan yalnız açık sayfa istemcide durur — sıralama tüm kayıt kümesine uygulanmalıdır.
 *
 * Yetki: `TEMPLATE_PARAMETER_MANAGE` (menü görünürlüğü); okuma uçları ayrıca şablon yetkisini de
 * kabul eder ama bu ekran menüde yalnız kendi yetkisiyle görünür.
 *
 * <p>Kısıtlı Yetkili asistan (126) ekranı <b>salt okunur</b> görür. Parametreler sekmesinde kalem
 * yerine <b>Görüntüle</b> gelir (açıklama ve statik içerik yalnız pop-up'ta görünür → kayıt aynı tekil
 * uçtan yüklenip pop-up salt okunur açılır). Gruplar sekmesinde Düzenle çizilmez, durum anahtarı pasif
 * kalır (ad ve durum listede zaten görünür).
 */
export default function TemplateParametersAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const toast = useToast()
  const { readOnly } = useProxyMode()

  const [activeTab, setActiveTab] = useState(TAB_PARAMETERS)

  // Parametreler sekmesi durumu
  const [groupId, setGroupId] = useState<number>(ALL)
  const [paramFirst, setParamFirst] = useState(0)
  const [paramRows, setParamRows] = useState(PAGE_SIZE)
  // Sıralama değişince ilk sayfaya dönülür: eski sayfa numarası yeni düzende başka kayıtlara denk gelir.
  const paramSort = useTableSort(() => setParamFirst(0))

  const { list, groupOptions, staticOptions, update } = useTemplateParameters({
    groupId: groupId === ALL ? null : groupId,
    page: Math.floor(paramFirst / paramRows),
    size: paramRows,
    sort: paramSort.sort,
  })

  const params = list.data?.content ?? []
  const paramTotal = list.data?.page.totalElements ?? 0
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  /** Düzenlenen parametre; `null` = kapalı. Ekleme YOKTUR → `'new'` durumu da yoktur. */
  const [editingParam, setEditingParam] = useState<TemplateParameter | null>(null)
  const [loadingRow, setLoadingRow] = useState(false)
  /** Durum isteği süren satır (yalnız Gruplar sekmesi — parametrelerde durum değişmez). */
  const [busyId, setBusyId] = useState<number | null>(null)

  /**
   * Düzenleme formunu açar.
   *
   * **Hata YUTULMAZ:** React Query dışında bir çağrı olduğu için merkezî `onError` toast'ı (019)
   * devreye girmez; yakalanmazsa buton sessizce hiçbir şey yapmaz.
   */
  const openEditParam = async (row: TemplateParameter) => {
    setLoadingRow(true)
    try {
      setEditingParam(await getTemplateParameter(row.id))
    } catch {
      toast.show({
        severity: 'error',
        summary: t('common.errors.generic'),
        life: 5000,
      })
    } finally {
      setLoadingRow(false)
    }
  }

  /**
   * Gönderim gövdesi. **Grup, parametre kodu ve "Statik mi?" formdan DEĞİL, düzenlenen kaydın
   * kendisinden** gelir: ekranda salt-okunur gösterilirler ama sunucu sözleşmesi
   * (`TemplateParameterUpsertDto`) onları zorunlu tutar → değişmemiş hâlleriyle geri gönderilirler.
   *
   * Formdan gelen tek koşullu alan `staticContent`'tir: kayıt statikse düzenlenebilir ve zorunludur,
   * değilse alan hiç render edilmez → boş gider ve sunucu zaten `null` yazar (FR-028).
   */
  const toParamBody = (
    row: TemplateParameter,
    values: TemplateParameterFormValues,
  ): TemplateParameterUpsert => ({
    templateParamGroupId: row.templateParamGroupId,
    nameTr: values.nameTr,
    nameEn: values.nameEn.trim() === '' ? null : values.nameEn,
    descriptionTr: values.descriptionTr.trim() === '' ? null : values.descriptionTr,
    descriptionEn: values.descriptionEn.trim() === '' ? null : values.descriptionEn,
    parameterCode: row.parameterCode,
    isStatic: row.isStatic,
    staticContent: values.staticContent.trim() === '' ? null : values.staticContent,
  })

  const onParamSubmit = (values: TemplateParameterFormValues) => {
    if (!editingParam) return
    const body = toParamBody(editingParam, values)

    update.mutate(
      { id: editingParam.id, body: { ...body, modDate: editingParam.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('admin.templateParameters.toast.updated'),
            life: 3000,
          })
          setEditingParam(null)
        },
        // Optimistic-lock/generic toast'ı merkezî hata yönetimi (019) basar; ekran yalnız tazeler.
        onError: (err) => {
          if (isOptimisticLock(err)) {
            setEditingParam(null)
            list.refetch()
          }
        },
      },
    )
  }

  // ── Gruplar sekmesi ────────────────────────────────────────────────────────
  //
  // **Yalnız düzenleme** (2026-08-03 kararı): grup ekleme ve silme bu ekranda YOKTUR — gruplar
  // parametrelerin bağlı olduğu sabit bir sınıflandırmadır. Kullanımdan kaldırma için durum anahtarı
  // (Aktif/Pasif) kullanılır; silmenin aksine geri alınabilir ve mevcut parametrelerin bağı kopmaz.
  // Bu yüzden `editingGroup` yalnız bir **kayıt** ya da `null` tutar; `'new'` durumu kaldırıldı.
  const [groupFirst, setGroupFirst] = useState(0)
  const [groupRows, setGroupRows] = useState(PAGE_SIZE)
  const [editingGroup, setEditingGroup] = useState<TemplateParamGroup | null>(null)
  // Parametreler sekmesinden BAĞIMSIZ sıralama durumu (FR-022: her sekme kendi durumunu korur).
  const groupSort = useTableSort(() => setGroupFirst(0))

  // Liste YALNIZ Gruplar sekmesi açıkken çekilir (research D12) — sekmeye girilene kadar istek yok.
  const groups = useTemplateParamGroups(
    { page: Math.floor(groupFirst / groupRows), size: groupRows, sort: groupSort.sort },
    activeTab === TAB_GROUPS,
  )

  const onGroupSubmit = (values: TemplateParamGroupFormValues) => {
    if (!editingGroup) return
    const body: TemplateParamGroupUpsert = {
      nameTr: values.nameTr,
      nameEn: values.nameEn.trim() === '' ? null : values.nameEn,
    }

    groups.update.mutate(
      { id: editingGroup.id, body: { ...body, modDate: editingGroup.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('admin.templateParameters.groups.toast.updated'),
            life: 3000,
          })
          setEditingGroup(null)
        },
        // Optimistic-lock/generic toast'ı merkezî hata yönetimi (019) basar; ekran yalnız tazeler.
        onError: (err) => {
          if (isOptimisticLock(err)) {
            setEditingGroup(null)
            groups.list.refetch()
          }
        },
      },
    )
  }

  const onGroupToggle = (row: TemplateParamGroup, active: boolean) => {
    setBusyId(row.id)
    groups.changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () =>
          toast.show({
            severity: 'success',
            summary: t('admin.templateParameters.groups.toast.statusChanged'),
            life: 3000,
          }),
        onSettled: () => setBusyId(null),
      },
    )
  }

  const groupFilterOptions = useMemo(
    () => [
      { label: t('admin.templateParameters.filter.groupAll'), value: ALL },
      ...(groupOptions.data ?? []).map((g) => ({ label: g.name, value: g.id })),
    ],
    [groupOptions.data, t],
  )

  const hasParamFilter = groupId !== ALL

  return (
    <div className="admin-page">
      <TemplateParameterFormDialog
        visible={editingParam != null}
        initial={editingParam}
        staticOptions={staticOptions.data ?? []}
        submitting={update.isPending}
        onSubmit={onParamSubmit}
        onHide={() => setEditingParam(null)}
        readOnly={readOnly}
      />

      <TemplateParamGroupFormDialog
        visible={editingGroup != null}
        initial={editingGroup}
        submitting={groups.update.isPending}
        onSubmit={onGroupSubmit}
        onHide={() => setEditingGroup(null)}
      />

      {/*
        Sayfada birincil aksiyon YOKTUR: iki sekmenin de ekleme yeteneği kaldırıldı (parametre 2026-08-04,
        grup 2026-08-03) → buton da gösterilmez; "var ama çalışmıyor" gibi bir ara durum bırakılmaz.
      */}
      {/* "Yenile" + "Excel'e Aktar" BAŞLIK satırında — diğer dokuz liste ekranıyla aynı kalıp
          (kullanıcı isteği 2026-08-27: "diğer yerlerle bütünlüğü sağlamak adına").

          Bu sayfanın eski gerekçesi "iki sekme var, başlıkta tek yer olduğu için hangi tablonun
          indirildiği belirsizleşir" idi. Çözüm belirsizliği KALDIRIYOR: aksiyonlar AKTİF SEKMEYE
          göre çizilir, yani aynı anda yalnız görünen tablonun butonu vardır. Sayfa iki listeyi de
          kendisi tuttuğu için portala (`PageHeaderActionsSlot`) gerek yok.

          Yan kazanç: 26.08'de eklenen "Excel ile Tüm gruplar yer değiştirdi" düzenlemesi ve onun
          için açılan `--plain`/`--attached` değiştiricileri artık gereksiz — süzgeç kendi doğal
          yerine (FilterBar) döndü ve kart görünümünü standart sınıf taşıyor. */}
      <PageHeader
        title={t('nav.admin.templateParameters')}
        actions={
          activeTab === 0 ? (
            <>
              <RefreshButton onRefresh={() => list.refetch()} />
              <ExcelExportButton
                onExport={() =>
                  exportTemplateParameters(groupId === ALL ? undefined : groupId, paramSort.sort)
                }
                disabled={params.length === 0}
              />
            </>
          ) : (
            <>
              <RefreshButton onRefresh={() => groups.list.refetch()} />
              <ExcelExportButton
                onExport={() => exportTemplateParamGroups(groupSort.sort)}
                disabled={(groups.list.data?.content ?? []).length === 0}
              />
            </>
          )
        }
      />

      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header={t('admin.templateParameters.tabs.parameters')}>
          {/* Süzgeç kendi doğal yerinde: FilterBar. "Filtreleri Temizle" bileşenin kendi kuralıyla
              dropdown'ın soluna düşer; kart görünümünü de standart `.ui-filterbar` taşır. */}
          <FilterBar
            showClear={hasParamFilter}
            clearLabel={t('admin.templateParameters.filter.clear')}
            onClear={() => {
              setGroupId(ALL)
              setParamFirst(0)
            }}
          >
            <Dropdown
              value={groupId}
              options={groupFilterOptions}
              onChange={(e) => {
                setGroupId((e.value as number | null) ?? ALL)
                setParamFirst(0)
              }}
              aria-label={t('admin.templateParameters.filter.groupLabel')}
              disabled={groupOptions.isError}
              scrollHeight="28rem"
            />
          </FilterBar>

          {isMobile ? (
            <div className="ui-card-list">
              {list.isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
                ))}
              {!list.isLoading && params.length === 0 && (
                <EmptyState
                  title={t('admin.templateParameters.empty.title')}
                  description={t('admin.templateParameters.empty.desc')}
                />
              )}
              {!list.isLoading &&
                params.map((row) => {
                  const active = row.recordStatus === 1
                  return (
                    <div key={row.id} className="ui-card">
                      <div className="ui-card-top">
                        <Badge tone={active ? 'green' : 'neutral'}>
                          {active
                            ? t('admin.templateParameters.status.active')
                            : t('admin.templateParameters.status.passive')}
                        </Badge>
                        <div className="ui-card-actions ui-card-actions--compact">
                          <Button
                            icon={readOnly ? 'pi pi-eye' : 'pi pi-pencil'}
                            rounded
                            text
                            disabled={loadingRow}
                            /* Erişilebilir ad KAYDI da söyler (THY AI review, 26.10.2026 — ikinci tur). Projenin kendi
   yardımcısı `rowActionLabel` kullanılır: ad boşsa adsız anahtara düşer. */
                            aria-label={rowActionLabel(
                              t,
                              readOnly
                                ? 'admin.templateParameters.actions.view'
                                : 'admin.templateParameters.actions.edit',
                              localized(row.nameTr, row.nameEn),
                            )}
                            onClick={() => openEditParam(row)}
                          />
                        </div>
                      </div>
                      <div className="ui-card-head">
                        <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                      </div>
                      <span className="ui-card-sub">{row.templateParamGroupName}</span>
                      <div className="ui-card-row">
                        <code className="admin-param-code">{row.parameterCode}</code>
                        {row.isStaticName && <span>{row.isStaticName}</span>}
                      </div>
                    </div>
                  )
                })}
              {!list.isLoading && paramTotal > paramRows && (
                <Paginator
                  first={paramFirst}
                  rows={paramRows}
                  totalRecords={paramTotal}
                  onPageChange={(e: PaginatorPageChangeEvent) => {
                    setParamFirst(e.first)
                    setParamRows(e.rows)
                  }}
                  template="PrevPageLink CurrentPageReport NextPageLink"
                  currentPageReportTemplate={t('common.pageReport')}
                  className="ui-cards-paginator"
                />
              )}
            </div>
          ) : (
          <DataTable
            value={params}
            loading={list.isLoading}
            lazy
            first={paramFirst}
            rows={paramRows}
            totalRecords={paramTotal}
            onPage={(e) => {
              setParamFirst(e.first)
              setParamRows(e.rows)
            }}
            sortField={paramSort.sortField}
            sortOrder={paramSort.sortOrder}
            onSort={paramSort.onSort}
            emptyMessage={
              <EmptyState
                title={t('admin.templateParameters.empty.title')}
                description={t('admin.templateParameters.empty.desc')}
              />
            }
          >
            {templateParameterColumns({
              t,
              localized,
              onEdit: openEditParam,
              busy: loadingRow,
              readOnly,
            })}
          </DataTable>
          )}
        </TabPanel>

        <TabPanel header={t('admin.templateParameters.tabs.groups')}>

          {/* Kendi sayfa durumu; sorgu `enabled` ile bu sekme açılana kadar hiç çalışmaz. */}
          {isMobile ? (
            <div className="ui-card-list">
              {groups.list.isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
                ))}
              {!groups.list.isLoading && (groups.list.data?.content ?? []).length === 0 && (
                <EmptyState
                  title={t('admin.templateParameters.groups.empty.title')}
                  description={t('admin.templateParameters.groups.empty.desc')}
                />
              )}
              {!groups.list.isLoading &&
                (groups.list.data?.content ?? []).map((row) => {
                  const active = row.recordStatus === 1
                  return (
                    <div key={row.id} className="ui-card">
                      <div className="ui-card-top">
                        <Badge tone={active ? 'green' : 'neutral'}>
                          {active
                            ? t('admin.templateParameters.status.active')
                            : t('admin.templateParameters.status.passive')}
                        </Badge>
                        <div className="ui-card-actions ui-card-actions--compact">
                          <InputSwitch
                            className="admin-switch"
                            checked={active}
                            disabled={readOnly || busyId === row.id}
                            onChange={(e) => onGroupToggle(row, Boolean(e.value))}
                            aria-label={row.nameTr}
                            tooltip={
                              readOnly ? undefined : t('admin.templateParameters.actions.toggle')
                            }
                            tooltipOptions={{ position: 'bottom' }}
                          />
                          {!readOnly && (
                            <Button
                              icon="pi pi-pencil"
                              rounded
                              text
                              aria-label={rowActionLabel(t, 'admin.templateParameters.actions.edit', localized(row.nameTr, row.nameEn))}
                              onClick={() => setEditingGroup(row)}
                            />
                          )}
                        </div>
                      </div>
                      <div className="ui-card-head">
                        <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                      </div>
                    </div>
                  )
                })}
              {!groups.list.isLoading &&
                (groups.list.data?.page.totalElements ?? 0) > groupRows && (
                  <Paginator
                    first={groupFirst}
                    rows={groupRows}
                    totalRecords={groups.list.data?.page.totalElements ?? 0}
                    onPageChange={(e: PaginatorPageChangeEvent) => {
                      setGroupFirst(e.first)
                      setGroupRows(e.rows)
                    }}
                    template="PrevPageLink CurrentPageReport NextPageLink"
                    currentPageReportTemplate={t('common.pageReport')}
                    className="ui-cards-paginator"
                  />
                )}
            </div>
          ) : (
          <DataTable
            value={groups.list.data?.content ?? []}
            loading={groups.list.isLoading}
            lazy
            first={groupFirst}
            rows={groupRows}
            totalRecords={groups.list.data?.page.totalElements ?? 0}
            onPage={(e) => {
              setGroupFirst(e.first)
              setGroupRows(e.rows)
            }}
            sortField={groupSort.sortField}
            sortOrder={groupSort.sortOrder}
            onSort={groupSort.onSort}
            emptyMessage={
              <EmptyState
                title={t('admin.templateParameters.groups.empty.title')}
                description={t('admin.templateParameters.groups.empty.desc')}
              />
            }
          >
            {templateParamGroupColumns({
              t,
              localized,
              onEdit: setEditingGroup,
              onToggle: onGroupToggle,
              busyId,
              readOnly,
            })}
          </DataTable>
          )}
        </TabPanel>
      </TabView>
    </div>
  )
}
